import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { api, resolveMediaUrl } from "@/lib/api";
import { toEmbedUrl, toWatchUrl, detectProvider } from "@/lib/video";
import { Reveal } from "@/components/Reveal";

const PLACEHOLDER_COVERS = [
  "https://images.unsplash.com/photo-1766153550988-121007d31002",
  "https://images.pexels.com/photos/10194706/pexels-photo-10194706.jpeg",
];

export default function Films() {
  const [films, setFilms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/films")
      .then(({ data }) => setFilms(data))
      .catch(() => setFilms([]))
      .finally(() => setLoading(false));
  }, []);

  const close = useCallback(() => setActive(null), []);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") close(); }
    if (active) window.addEventListener("keydown", onKey);
    document.body.style.overflow = active ? "hidden" : "";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close]);

  const showEmpty = !loading && films.length === 0;

  return (
    <div data-testid="films-page" className="bg-[#1A1514] min-h-screen">
      <section className="pt-40 md:pt-48 px-6 md:px-12 lg:px-16 max-w-7xl mx-auto">
        <Reveal>
          <p className="text-[10px] tracking-[0.42em] uppercase text-ink-taupe">Chapter I</p>
        </Reveal>
        <Reveal delay={150}>
          <h1
            data-testid="films-title"
            className="mt-6 font-display text-6xl md:text-8xl lg:text-9xl leading-none tracking-tighter text-ink-ivory"
          >
            Films
          </h1>
        </Reveal>
        <Reveal delay={350}>
          <p className="mt-10 max-w-xl text-base md:text-lg leading-relaxed text-ink-ivory/65">
            A cinematic archive of weddings, slowly composed. Each film is a quiet
            study of a single day — its light, its silences, its small confessions.
          </p>
        </Reveal>
      </section>

      <section className="px-6 md:px-12 lg:px-16 py-24 md:py-32 max-w-7xl mx-auto">
        {loading ? (
          <FilmsSkeleton />
        ) : showEmpty ? (
          <FilmsEmpty />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-y-24 md:gap-y-32 gap-x-8">
            {films.map((film, i) => (
              <FilmCard key={film.id} film={film} index={i} onOpen={() => setActive(film)} />
            ))}
          </div>
        )}
      </section>

      {active && <FilmPlayer film={active} onClose={close} />}
    </div>
  );
}

function FilmCard({ film, index, onOpen }) {
  // Asymmetric layout: alternate large left / large right with offset
  const isLeft = index % 2 === 0;
  const span = isLeft ? "md:col-span-8" : "md:col-span-8 md:col-start-5";
  const cover = resolveMediaUrl(film.cover_image) || film.thumbnail || PLACEHOLDER_COVERS[index % 2];

  return (
    <Reveal className={span} delay={index * 80}>
      <button
        type="button"
        onClick={onOpen}
        data-testid={`film-card-${film.id}`}
        className="block w-full text-left group"
        aria-label={`Play film: ${film.title}`}
      >
        <div className="img-hover relative aspect-cinema overflow-hidden border border-ink-ivory/10 bg-[#221B1A]">
          <img src={cover} alt={film.title} className="w-full h-full object-cover" />
          <div
            className="absolute inset-0 transition-opacity duration-700 opacity-90 group-hover:opacity-70"
            style={{ background: "linear-gradient(180deg, rgba(26,21,20,0.20) 50%, rgba(26,21,20,0.85) 100%)" }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <PlayMark />
          </div>
          <div className="absolute left-6 right-6 bottom-6 flex items-end justify-between gap-4">
            <div>
              <p className="text-[10px] tracking-[0.32em] uppercase text-ink-champagne">
                {String(index + 1).padStart(2, "0")} · Film
              </p>
              <h3 className="mt-2 font-display text-3xl md:text-5xl tracking-tight text-ink-ivory">{film.title}</h3>
            </div>
            <div className="hidden md:block text-right">
              {film.couple && <p className="text-xs tracking-[0.16em] uppercase text-ink-ivory/70">{film.couple}</p>}
              {(film.location || film.year) && (
                <p className="mt-1 text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
                  {[film.location, film.year].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>
          </div>
        </div>
        {film.description && (
          <p className="mt-6 text-sm md:text-base text-ink-ivory/65 leading-relaxed max-w-2xl">
            {film.description}
          </p>
        )}
      </button>
    </Reveal>
  );
}

function PlayMark() {
  return (
    <span
      className="inline-flex items-center gap-3 px-5 py-3 border border-ink-ivory/40 text-ink-ivory transition-all duration-700 group-hover:bg-ink-ivory group-hover:text-ink-espresso"
      aria-hidden="true"
    >
      <svg width="10" height="12" viewBox="0 0 10 12" fill="currentColor">
        <path d="M0 0L10 6L0 12V0Z" />
      </svg>
      <span className="text-[10px] tracking-[0.32em] uppercase">Play Film</span>
    </span>
  );
}

function FilmPlayer({ film, onClose }) {
  // Normalise whatever is stored — video_url or pre-computed embed_url — into a clean embed.
  const embedUrl = toEmbedUrl(film.video_url) || toEmbedUrl(film.embed_url);
  const provider = detectProvider(film.video_url) || detectProvider(film.embed_url);
  const watchUrl = toWatchUrl(film.video_url) || toWatchUrl(film.embed_url) || film.video_url;

  // Defer iframe mount until the modal fade has settled — prevents stuttery autoplay.
  const [mountIframe, setMountIframe] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (!embedUrl) return;
    const t = setTimeout(() => setMountIframe(true), 320);
    return () => clearTimeout(t);
  }, [embedUrl]);

  // Listen for YouTube IFrame API postMessage errors (video unavailable, embed disabled,
  // codec fallback → audio only, geo-block, etc.). If YouTube reports an error we surface
  // the fallback panel instead of leaving the user with a silent black rectangle.
  useEffect(() => {
    if (!mountIframe) return;
    function onMessage(e) {
      const origin = typeof e.origin === "string" ? e.origin : "";
      if (!/(^|\.)youtube(-nocookie)?\.com$/i.test(new URL(origin || "https://x").hostname)) return;
      let payload = e.data;
      if (typeof payload === "string") {
        try { payload = JSON.parse(payload); } catch { return; }
      }
      if (!payload || typeof payload !== "object") return;
      const errorCode =
        (payload.info && typeof payload.info === "object" && payload.info.errorCode) ||
        (payload.event === "onError" ? payload.info : null);
      if (errorCode !== null && errorCode !== undefined) {
        // 2 = invalid param, 5 = HTML5, 100 = removed, 101/150 = embed disabled by owner
        setErrored(true);
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [mountIframe]);

  if (typeof document === "undefined") return null;

  const modal = (
    <div
      data-testid="film-player"
      className="animate-soft-fade"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 999999,
        // No backdrop-filter here — Safari clips iframe video layers when an ancestor
        // has backdrop-filter or mix-blend-mode. The blur is applied to a sibling
        // <div class="backdrop"/> BEHIND the stage instead.
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "clamp(16px, 4vw, 48px)",
        isolation: "isolate",
      }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop — blur/tint applied here so the stage's ancestor chain is filter-free */}
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          background: "rgba(15, 12, 11, 0.96)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
        }}
      />

      <button
        type="button"
        onClick={onClose}
        data-testid="film-player-close"
        aria-label="Close film"
        className="text-[10px] tracking-[0.32em] uppercase text-ink-ivory editorial-link"
        style={{ position: "absolute", top: 24, right: 24, zIndex: 3 }}
      >
        Close ✕
      </button>

      <div
        className="w-full"
        style={{
          maxWidth: "min(1200px, 100%)",
          position: "relative",
          zIndex: 2,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Strict 16:9 stage — no backdrop-filter ancestor, GPU-promoted for Safari. */}
        <div
          className="w-full"
          style={{
            position: "relative",
            aspectRatio: "16 / 9",
            background: "#000",
            // overflow:hidden intentionally omitted — Safari clips iframe video layers
            // to the padding box when combined with a border on the same element.
            border: "1px solid rgba(247,244,240,0.10)",
            transform: "translateZ(0)",
            WebkitTransform: "translateZ(0)",
            willChange: "transform",
            isolation: "isolate",
          }}
        >
          {!embedUrl ? (
            <PlayerFallback
              message="This video can't be embedded — open it on the platform instead."
              watchUrl={watchUrl}
            />
          ) : errored ? (
            <PlayerFallback
              message="The embed was refused by the host. You can still watch it on the platform."
              watchUrl={watchUrl}
            />
          ) : !mountIframe ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              className="text-ink-taupe text-[10px] tracking-[0.42em] uppercase"
            >
              Loading…
            </div>
          ) : (
            <iframe
              data-testid="film-player-iframe"
              src={embedUrl}
              title={film.title}
              frameBorder="0"
              loading="eager"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              onError={() => setErrored(true)}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                border: 0,
                display: "block",
                // GPU-promote the iframe so Safari draws the WebKit video layer
                // in its own compositor plane and does not clip it behind ancestors.
                transform: "translateZ(0)",
                WebkitTransform: "translateZ(0)",
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
              }}
            />
          )}
        </div>

        <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl md:text-4xl tracking-tight text-ink-ivory">{film.title}</h2>
            {film.couple && <p className="mt-1 text-xs tracking-[0.16em] uppercase text-ink-ivory/70">{film.couple}</p>}
          </div>
          <div className="flex items-center gap-6 text-right">
            {(film.location || film.year) && (
              <p className="text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
                {[film.location, film.year].filter(Boolean).join(" · ")}
              </p>
            )}
            {watchUrl && provider && (
              <a
                href={watchUrl}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="film-player-external"
                className="text-[10px] tracking-[0.32em] uppercase text-ink-champagne editorial-link"
              >
                Open on {provider === "youtube" ? "YouTube" : "Vimeo"} ↗
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

function PlayerFallback({ message, watchUrl }) {
  return (
    <div
      data-testid="film-player-fallback"
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 24,
        padding: 24,
        background: "#0F0C0B",
      }}
      className="text-center"
    >
      <p className="text-[10px] tracking-[0.42em] uppercase text-ink-champagne">Unavailable for embed</p>
      <p className="max-w-md text-sm md:text-base text-ink-ivory/80 leading-relaxed">{message}</p>
      {watchUrl && (
        <a
          href={watchUrl}
          target="_blank"
          rel="noreferrer noopener"
          className="luxe-btn"
        >
          Watch the film ↗
        </a>
      )}
    </div>
  );
}

function FilmsSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-y-32 gap-x-8">
      {[0, 1, 2].map((i) => (
        <div key={i} className={`${i % 2 === 0 ? "md:col-span-8" : "md:col-span-8 md:col-start-5"}`}>
          <div className="aspect-cinema bg-[#1F1817] border border-ink-ivory/10 animate-pulse" />
        </div>
      ))}
    </div>
  );
}

function FilmsEmpty() {
  return (
    <div data-testid="films-empty" className="border border-dashed border-ink-ivory/15 px-6 md:px-16 py-24 md:py-32 text-center max-w-3xl mx-auto">
      <p className="text-[10px] tracking-[0.42em] uppercase text-ink-champagne">Curating</p>
      <h2 className="mt-6 font-display text-3xl md:text-5xl tracking-tight text-ink-ivory">
        The reel is being prepared.
      </h2>
      <p className="mt-8 text-base text-ink-ivory/65 leading-relaxed">
        Films from the studio&rsquo;s current season are being sequenced and graded.
        New work is added quietly, like pages bound into a book.
      </p>
      <p className="mt-10 text-[10px] tracking-[0.32em] uppercase text-ink-taupe">
        Volume I — In Preparation
      </p>
    </div>
  );
}
